<template>
  <v-navigation-drawer
    v-if="shouldShow && isOpen"
    :value="true"
    disable-resize-watcher
    left
    temporary
    stateless
    hide-overlay
    dark
    color="secondary"
    width="400"
    class="saved-searches-drawer"
    :style="drawerLayoutStyle"
  >
    <div
      ref="drawerInner"
      class="saved-searches-drawer__inner"
      tabindex="-1"
      @mousedown="onDrawerMouseDown"
    >
      <favourites dark @closeDrawer="close" />
    </div>
  </v-navigation-drawer>
</template>

<script>
import Favourites from '../Favourites.vue';
import {
  NAVIGATION_DRAWER_MINI_WIDTH,
  NAVIGATION_DRAWER_WIDTH,
} from './navigationDrawerLayout.js';

export default {
  name: 'SavedSearchesDrawer',

  components: {
    Favourites,
  },

  props: {
    shouldShow: {
      type: Boolean,
      default: false,
    },
    navigationDrawerOpen: {
      type: Boolean,
      default: true,
    },
    navigationDrawerMini: {
      type: Boolean,
      default: false,
    },
    rightDrawerOpen: {
      type: Boolean,
      default: false,
    },
    advancedSearchOpen: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isOpen: false,
      outsideClickListening: false,
      onOutsidePointerDown: null,
    };
  },

  computed: {
    navDrawerOffset() {
      if (!this.navigationDrawerOpen) {
        return 0;
      }
      if (this.navigationDrawerMini) {
        return NAVIGATION_DRAWER_MINI_WIDTH;
      }
      return NAVIGATION_DRAWER_WIDTH;
    },

    drawerLayoutStyle() {
      return {
        '--nav-drawer-offset': `${this.navDrawerOffset}px`,
      };
    },
  },

  watch: {
    isOpen(val) {
      this.$emit('open-change', val);

      if (!val) {
        this.removeOutsideClickListener();
        return;
      }
      this.addOutsideClickListener();
    },

    rightDrawerOpen(isOpen) {
      if (isOpen) {
        this.close();
      }
    },

    advancedSearchOpen(isOpen) {
      if (isOpen) {
        this.close();
      }
    },
  },

  beforeDestroy() {
    this.removeOutsideClickListener();
  },

  methods: {
    open() {
      this.isOpen = true;
      this.$emit('close-utility-drawers');
    },

    close() {
      this.isOpen = false;
    },

    toggle() {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    },

    onDrawerMouseDown() {
      const el = this.$refs.drawerInner;
      if (el && document.activeElement !== el) {
        el.focus({ preventScroll: true });
      }
    },

    isPointerInDrawer(target) {
      return Boolean(
        target && target.closest && target.closest('.saved-searches-drawer')
      );
    },

    isPointerInNavigationDrawer(target) {
      return Boolean(
        target && target.closest && target.closest('.app-navigation-drawer')
      );
    },

    addOutsideClickListener() {
      if (this.outsideClickListening) {
        return;
      }

      this.onOutsidePointerDown = (e) => {
        if (!this.isOpen) {
          return;
        }

        const inDrawer = this.isPointerInDrawer(e.target);
        const inNavDrawer = this.isPointerInNavigationDrawer(e.target);
        const inMenu = e.target.closest && e.target.closest('.menuable__content__active');

        if (inDrawer || inNavDrawer || inMenu) {
          return;
        }

        this.close();
      };

      document.addEventListener('pointerdown', this.onOutsidePointerDown, true);
      this.outsideClickListening = true;
    },

    removeOutsideClickListener() {
      if (!this.outsideClickListening) {
        return;
      }

      document.removeEventListener('pointerdown', this.onOutsidePointerDown, true);
      this.outsideClickListening = false;
    },
  },
};
</script>

<style>
/*
 * Left secondary panel beside the nav drawer.
 * Must be position:fixed — temporary drawers without `app` do not get
 * .v-navigation-drawer--fixed, so a closed/hidden drawer was pushing v-main down.
 */
.saved-searches-drawer {
  position: fixed !important;
  left: var(--nav-drawer-offset, 260px) !important;
  top: var(--app-header-height, 64px) !important;
  height: calc(100% - var(--app-header-height, 64px)) !important;
  max-height: calc(100% - var(--app-header-height, 64px)) !important;
  z-index: 4;
  transform: none !important;
}

.saved-searches-drawer >>> .v-navigation-drawer__content {
  height: 100%;
  overflow: hidden;
  background: #12253a;
}

.saved-searches-drawer__inner {
  height: 100%;
  overflow: hidden;
}
</style>
